import React from "react";
import type { OrModalMobileBehavior, OrModalSize, OrModalVariant } from "./or-modal";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getAttribute("aria-hidden") !== "true",
  );
}

export interface UseOrModalParams {
  className?: string;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  mobileBehavior?: OrModalMobileBehavior;
  onClose: () => void;
  open: boolean;
  restoreFocus?: boolean;
  size?: OrModalSize;
  variant?: OrModalVariant;
}

export interface UseOrModalResult {
  dialogClassName: string;
  dialogRef: React.MutableRefObject<HTMLDivElement | null>;
  handleDialogKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  handleOverlayClick: () => void;
  headerCloseButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  modalClassName: string;
}

export function useOrModal({
  className,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  initialFocusRef,
  mobileBehavior = "bottom-sheet",
  onClose,
  open,
  restoreFocus = true,
  size = "md",
  variant = "default",
}: UseOrModalParams): UseOrModalResult {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const headerCloseButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement) {
      previousFocusRef.current = activeElement;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timeoutId = window.setTimeout(() => {
      const focusTarget =
        initialFocusRef?.current ??
        getFocusableElements(dialogRef.current)[0] ??
        headerCloseButtonRef.current ??
        dialogRef.current;

      focusTarget?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.body.style.overflow = originalOverflow;

      if (restoreFocus) {
        previousFocusRef.current?.focus();
      }
    };
  }, [initialFocusRef, open, restoreFocus]);

  React.useEffect(() => {
    if (!open || !closeOnEscape || typeof document === "undefined") {
      return undefined;
    }

    const handleDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleDocumentKeyDown);

    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [closeOnEscape, onClose, open]);

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Tab") {
      return;
    }

    const focusableElements = getFocusableElements(dialogRef.current);

    if (focusableElements.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const currentFocus = event.target instanceof HTMLElement ? event.target : null;

    if (event.shiftKey && currentFocus === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && currentFocus === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleOverlayClick = (): void => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  const modalClassName = ["or-modal", `or-modal--${variant}`, open ? "or-modal--open" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  const dialogClassName = ["or-modal__dialog", `or-modal__dialog--${size}`, `or-modal__dialog--${mobileBehavior}`]
    .filter(Boolean)
    .join(" ");

  return {
    dialogClassName,
    dialogRef,
    handleDialogKeyDown,
    handleOverlayClick,
    headerCloseButtonRef,
    modalClassName,
  };
}
